import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTextAnnotationDialogStore } from '../../store/textAnnotationDialogStore';
import './TextAnnotationDialogComponent.css';
import { CloseIcon } from '../Icons';

const FONT_SIZE_OPTIONS = [
    { value: '0.02', labelKey: 'command.addTextAnnotation.dialog.fontSizeSmallest' },
    { value: '0.045', labelKey: 'command.addTextAnnotation.dialog.fontSizeSmall' },
    { value: '0.07', labelKey: 'command.addTextAnnotation.dialog.fontSizeMedium' },
    { value: '0.15', labelKey: 'command.addTextAnnotation.dialog.fontSizeLarge' },
] as const;

const TextAnnotationDialogComponent = () => {
    const { t } = useTranslation();
    const { isOpen, confirm, cancel } = useTextAnnotationDialogStore();
    const [note, setNote] = useState('');
    const [fontSize, setFontSize] = useState('0.07');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (isOpen) {
            setNote('');
            setFontSize('0.07');
            setTimeout(() => textareaRef.current?.focus(), 0);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const canConfirm = note.trim().length > 0;

    const handleConfirm = () => {
        if (!canConfirm) return;
        confirm({ note, fontSize: parseFloat(fontSize) });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            cancel();
        }
    };

    const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleConfirm();
        }
    };

    const handleSegmentKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            const next = (index + 1) % FONT_SIZE_OPTIONS.length;
            setFontSize(FONT_SIZE_OPTIONS[next].value);
            (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = (index - 1 + FONT_SIZE_OPTIONS.length) % FONT_SIZE_OPTIONS.length;
            setFontSize(FONT_SIZE_OPTIONS[next].value);
            (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
        }
    };

    return (
        <div className="text-annotation-dialog-overlay" onClick={cancel}>
            <div className="text-annotation-dialog" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
                <div className="text-annotation-dialog-header">
                    <div className="text-annotation-dialog-header-left">
                        <span className="text-annotation-dialog-status-dot" />
                        <span className="text-annotation-dialog-title">{t('command.addTextAnnotation.title')}</span>
                    </div>
                    <button className="text-annotation-dialog-close" onClick={cancel} aria-label={t('common.close')}>
                        <CloseIcon />
                    </button>
                </div>

                <div className="text-annotation-dialog-body">
                    <div className="text-annotation-dialog-group">
                        <div className="text-annotation-dialog-section-title">
                            {t('command.addTextAnnotation.dialog.noteTextSection')}
                        </div>
                        <textarea
                            ref={textareaRef}
                            rows={4}
                            value={note}
                            placeholder={t('command.addTextAnnotation.dialog.notePlaceholder')}
                            onChange={(e) => setNote(e.target.value)}
                            onKeyDown={handleTextareaKeyDown}
                        />
                    </div>

                    <div className="text-annotation-dialog-group">
                        <div className="text-annotation-dialog-section-title">
                            {t('command.addTextAnnotation.dialog.fontSize')}
                        </div>
                        <div
                            className="text-annotation-dialog-segmented"
                            role="radiogroup"
                            aria-label={t('command.addTextAnnotation.dialog.fontSize')}
                        >
                            {FONT_SIZE_OPTIONS.map(({ value, labelKey }, index) => {
                                const selected = fontSize === value;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        tabIndex={selected ? 0 : -1}
                                        className={`text-annotation-dialog-segment${selected ? ' selected' : ''}`}
                                        onClick={() => setFontSize(value)}
                                        onKeyDown={(e) => handleSegmentKeyDown(e, index)}
                                    >
                                        {t(labelKey)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="text-annotation-dialog-footer">
                    <button className="text-annotation-dialog-btn-cancel" onClick={cancel}>
                        {t('common.cancel')}
                    </button>
                    <button
                        className="text-annotation-dialog-btn-primary"
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                    >
                        {t('command.addTextAnnotation.dialog.addNote')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TextAnnotationDialogComponent;
